import { ServiceFromServer } from "./service";

export function formatSpecs(service: ServiceFromServer) {
    function planCodeConverter(group: number) {
        if (group === 61) {
            // "Standard (Promo)"
            group = 2;
        }
        if ([63, 64].includes(group)) {
            // "Premium (Promo)"
            group = 3;
        }
        return JSON.parse(JSON.stringify(modelSpecs[{
            1: "Free",
            2: "Standard",
            3: "Premium",
            50: "Unlimited",
            51: "Standard (Perpetual License)",
            52: "Premium (Perpetual License)",
        }[group]]));
    }

    let currentPlan = planCodeConverter(service.group);

    function numberToStringWithUnits(num: number) {
        if (num === 0) return "0";
        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
        return num.toString();
    }

    const databaseUsage = (service.storage_info?.database || 0) + (service.storage_info?.cloud || 0);

    let serviceStatus = {
        id: service.formattedServiceId,
        original_service_id: service.service,
        statusCode: service.active,
        name: service.name,
        status: {
            "0": "Disabled",
            "1": "Running",
            "-1": "Suspended",
            "-2": "Deleted",
            "-999": "Banned"
        }[service.active.toString()],
        canChangePlan: !currentPlan.label.includes("Perpetual") && currentPlan.label !== "Unlimited",
        users_string: numberToStringWithUnits(service.users),
        users_percent: `${getPercentUsed(service.users, currentPlan.users === 0 ? 0 : currentPlan.users)}%`,
        users: service.users,
        newsletter_subscribers_string: numberToStringWithUnits(service.newsletter_subscribers),
        newsletter_subscribers: service.newsletter_subscribers,
        storage_bytes: {
            ...service.storage_info,
            database: databaseUsage,
        },
        storage: {
            database: convertBytesToReadableStorage(databaseUsage),
            database_percent: `${getPercentUsed(databaseUsage, currentPlan.storage.database === "Unlimited" ? 0 : currentPlan.storage_bytes.database)}%`,
            cloud: convertBytesToReadableStorage(service.storage_info?.cloud),
            cloud_percent: `${getPercentUsed(service.storage_info?.cloud || 0, currentPlan.storage.cloud === "Unlimited" ? 0 : currentPlan.storage_bytes.cloud)}%`,
            email: convertBytesToReadableStorage(service.storage_info?.email),
            email_percent: `${getPercentUsed(service.storage_info?.email || 0, currentPlan.storage.email === "Unlimited" ? 0 : currentPlan.storage_bytes.email)}%`,
            host: convertBytesToReadableStorage(service.storage_info?.host),
            host_percent: `${getPercentUsed(service.storage_info?.host || 0, currentPlan.storage.host === "Unlimited" ? 0 : currentPlan.storage_bytes.host)}%`,
            file: convertBytesToReadableStorage(service.storage_info?.file),
            file_percent: `${getPercentUsed(service.storage_info?.file || 0, currentPlan.storage.file === "Unlimited" ? 0 : currentPlan.storage_bytes.file)}%`,
        }
    }
    return {
        currentPlan,
        serviceStatus
    };
}

function getPercentUsed(used: number, total: number) {
    if (total === 0) return 0;
    let percent = (used / total) * 100;
    if (used > 0 && percent < 1) return 1;
    return Math.round(percent);
}

export function convertBytesToReadableStorage(bytes: number) {
    if (!bytes) return "0B";
    if (bytes >= 1e12) return (bytes / 1e12).toFixed(2) + "TB";
    if (bytes >= 1e9) return (bytes / 1e9).toFixed(2) + "GB";
    if (bytes >= 1e6) return (bytes / 1e6).toFixed(2) + "MB";
    if (bytes >= 1e3) return (bytes / 1e3).toFixed(2) + "KB";
    return bytes + "B";
}

export const modelSpecs = ((m: any) => {
    function storageUnitConverter(storage: string) {
        // convert "1GB" to 1073741824
        if (storage === "Unlimited") return 0;

        let unit = storage.replace(/[\d.\s]/g, "").toUpperCase(); // "GB", "TB"
        let number = parseFloat(storage.replace(/[^\d.]/g, "")); // 1

        let multiplier = ({
            B: 1,
            KB: 1024,
            MB: 1024 ** 2,
            GB: 1024 ** 3,
            TB: 1024 ** 4,
            PB: 1024 ** 5,
        })[unit];

        if (!multiplier || Number.isNaN(number)) {
            return NaN;
        }

        return number * multiplier;
    }

    for (let k in m) {
        let item = m[k];
        item.storage_bytes = {
            database: storageUnitConverter(item.storage.database),
            cloud: storageUnitConverter(item.storage.cloud),
            host: storageUnitConverter(item.storage.host),
            email: storageUnitConverter(item.storage.email),
            file: storageUnitConverter(item.storage.file),
        }
        function convertUsers(users: number | string) {
            if (typeof users === "number") return users;
            if (typeof users === "string") {
                let canBeNumber = !isNaN(parseFloat(users));
                if (canBeNumber) return parseFloat(users);

                if (users.toLowerCase() === "unlimited") return 0;
                let multiplier = ({
                    k: 1e3,
                    m: 1e6,
                    b: 1e9,
                    t: 1e12,
                })[users.slice(-1).toLowerCase()];
                let number = parseFloat(users.slice(0, -1));
                if (!multiplier || Number.isNaN(number)) {
                    return NaN;
                }
                return number * multiplier;
            }
            return NaN;
        }
        item.users = convertUsers(item.users_string);
        item.offer_list = [];

        if (item.users > 0) {
            item.offer_list.push({
                class: "user",
                text: `${item.users_string} User Accounts`,
            });
        }

        if (item.storage_bytes.database > 0) {
            item.offer_list.push({
                class: "data",
                text: `${item.storage.database} Database Storage`,
            });
        }

        if (item.storage_bytes.cloud > 0) {
            item.offer_list.push({
                class: "file",
                text: `${item.storage.cloud} File Storage`,
            });
        }

        if (item.storage_bytes.email > 0) {
            item.offer_list.push({
                class: "mail",
                text: `${item.storage.email} Email Storage`,
            });
        }

        if (item.storage_bytes.host > 0) {
            item.offer_list.push({
                class: "global",
                text: `${item.storage.host} Web Hosting`,
            });
        }

        let label = item.label.toLowerCase();

        if(!label.includes("free")) {
            item.offer_list.push({
                class: "setting",
                text: "Register Unlimited Client Secret Keys",
            });
        }

        if (item.overages) {
            item.offer_list.push({
                class: "billing",
                text: "Pay-as-you-go overages for users, data, files, and emails (currently free)",
            });
        }
    }
    return m;
}
)({
    "Free": {
        price: 0,
        users_string: "3",
        storage: {
            database: "50MB",
            cloud: "100MB",
            email: "0B",
            host: "100MB",
            file: "100MB"
        },
        description: "Perfect for testing and small projects. Free forever.",
        label: "Free"
    },
    "Standard": {
        price: 19,
        users_string: "50K",
        storage: {
            database: "8GB",
            cloud: "100GB",
            email: "1GB",
            host: "100GB",
            file: "100GB"
        },
        description: "Ideal for most projects. Perfect for hobby projects and learning.",
        label: "Standard"
    },
    "Standard (Perpetual License)": {
        price: 299,
        users_string: "50K",
        storage: {
            database: "8GB",
            cloud: "100GB",
            email: "1GB",
            host: "100GB",
            file: "100GB"
        },
        description: "Ideal for most projects. One-time payment with lifetime access. Perfect for hobby projects and learning.",
        label: "Standard (Perpetual)"
    },
    "Premium": {
        price: 89,
        users_string: "100K",
        storage: {
            database: "100GB",
            cloud: "1TB",
            email: "10GB",
            host: "1TB",
            file: "1TB"
        },
        description: "Perfect for businesses and serious projects with priority support.",
        label: "Premium",
        overages: true
    },
    "Premium (Perpetual License)": {
        price: 1399,
        users_string: "100K",
        storage: {
            database: "100GB",
            cloud: "1TB",
            email: "10GB",
            host: "1TB",
            file: "1TB"
        },
        description: "For businesses and projects that require more resources and priority support. One-time payment with lifetime access.",
        label: "Premium (Perpetual)",
        overages: true
    },
    "Unlimited": {
        price: 599,
        users_string: "Unlimited",
        storage: {
            database: "Unlimited",
            cloud: "Unlimited",
            email: "Unlimited",
            host: "Unlimited",
            file: "Unlimited"
        },
        description: "For businesses and projects that require unlimited resources and priority support. Perfect for large-scale projects and enterprises.",
        label: "Unlimited"  
    }
})
